import uniqBy from 'lodash/uniqBy'
import { OverrideProperties } from 'type-fest'

import type { DipsResource } from '~/storm'

import { AnyPractitioner } from './practitioner'
import { AnySurgery } from './surgery'

/**
 * NOTE 1:
 * `surgeryResources` from DIPS are surgery resource slots and not the actual resources.
 * The slots may or may not be filled with the actual resources.
 */

/**
 * NOTE 2:
 * Identifying specific resource slots in the booking dialog to edit them is done dynamically based on the `slot.bookingId`
 * (i.e. the `id` of the slot, not to be confused with the `surgery.bookingId`).
 *
 * Identifying specific resource slots to get the value of a filled slot in other contexts, depends on hospital settings.
 * The only exception is `isTheaterRoomSlot`, which can be uniquely identified by the resourceTypeId/typeId `104605`.
 * `isMainSurgeonSlot` is more tricky as hospitals use both `220716` and `104600` for the main surgeon. However, DIPS have
 * in their docs (installation guide) that this slot must be named `Hovedoperatør`. Hence, it can be uniquely identified by
 * the `assignedResourceTypeName` property. `isAssistantSlot` and `isFirstAssistantSlot` etc. are even more tricky as more
 * or less all of them uses the same resourceTypeId/typeId (`104600`) and they can be named "whatever". Therefore, these
 * need to be tweaked to enable hospital-specific configurations if needed the future. Currently, we do not need to
 * differentiate between different types of assistants.
 *
 * Refer to: https://deepinsight-no.slack.com/archives/C04V3LBL7V0/p1730887666575479
 */

/**
 * Re-exported for callers that previously typed against both Storm and
 * Denormalizer `DipsResource`. The Denormalizer type is a structural subtype
 * of the Storm one (see §10 of the shallow-selectors docs), so the bare
 * Storm `DipsResource` is sufficient.
 */
export type AnyDipsResource = DipsResource

// Common utils
export type AnySurgeryResourceSlots = AnySurgery['surgeryResources']
export type AnySurgeryResourceSlot = NonNullable<AnySurgeryResourceSlots>[number]

export function getUpdatedResourceSlot(slot: AnySurgeryResourceSlot, resource: AnyDipsResource | null): AnySurgeryResourceSlot {
    return {
        ...slot,
        resourceId: resource ? resource.id : null,
        shortName: resource ? resource.shortName : null,
        name: resource ? resource.name : null,
    }
}

export function isFilledResourceSlot(slot: AnySurgeryResourceSlot): slot is AnySurgeryResourceSlot {
    return Boolean(slot.resourceId)
}

// Location/TheaterRoom utils
export const locationResourceTypeId = 104605 as const
type LocationResourceTypeId = typeof locationResourceTypeId
type LocationResourceSlot = OverrideProperties<AnySurgeryResourceSlot, { resourceTypeId: LocationResourceTypeId }>

export function isTheaterRoomSlot(slot: AnySurgeryResourceSlot): slot is LocationResourceSlot {
    return Boolean(slot.resourceTypeId === locationResourceTypeId)
}

export function getTheaterRoom(slots: AnySurgeryResourceSlots): LocationResourceSlot | undefined {
    return slots?.filter(isFilledResourceSlot).find(isTheaterRoomSlot)
}

// Practitioners utils
export const practitionerResourceTypeIds = [220716, 104600, 223475] as const
type PractitionerResourceTypeId = (typeof practitionerResourceTypeIds)[number]
export type PractitionerResourceSlot = OverrideProperties<AnySurgeryResourceSlot, { resourceTypeId: PractitionerResourceTypeId }>

export const practitionerResourceNames = {
    Surgeon: 220716,
    Assistant: 104600,
    FirstAssistant: 223475,
} as const satisfies Record<string, PractitionerResourceTypeId>

export function isMainSurgeonSlot(slot: AnySurgeryResourceSlot): slot is PractitionerResourceSlot {
    return Boolean(slot.assignedResourceTypeName === 'Hovedoperatør' || slot.resourceTypeId === practitionerResourceNames.Surgeon)
}

export function isAssistantSlot(slot: AnySurgeryResourceSlot): slot is PractitionerResourceSlot {
    return slot.resourceTypeId === practitionerResourceNames.Assistant || slot.resourceTypeId === practitionerResourceNames.FirstAssistant
}

export function isPractitionerSlot(slot: AnySurgeryResourceSlot) {
    return isAssistantSlot(slot) || isMainSurgeonSlot(slot)
}

export function getMainSurgeon(slots: AnySurgeryResourceSlots): PractitionerResourceSlot | undefined {
    return slots?.filter(isFilledResourceSlot).filter(isMainSurgeonSlot).at(0)
}

export function getAssistants(slots: AnySurgeryResourceSlots): PractitionerResourceSlot[] {
    return slots?.filter(isFilledResourceSlot).filter(isAssistantSlot) ?? []
}

export function getMainSurgeonAndAssistants(slots: AnySurgeryResourceSlots): PractitionerResourceSlot[] {
    const mainSurgeon = getMainSurgeon(slots)
    const assistants = getAssistants(slots)
    return mainSurgeon ? [mainSurgeon, ...assistants] : assistants
}

export function getAllMainSurgeonsAndAssistants(surgeries: AnySurgery[]): PractitionerResourceSlot[] {
    const allMainSurgeonsAndAssistants = surgeries.flatMap(surgery => getMainSurgeonAndAssistants(surgery.surgeryResources))
    const uniqueMainSurgeonsAndAssistants = uniqBy(allMainSurgeonsAndAssistants, 'shortName')
    return uniqueMainSurgeonsAndAssistants
}

export function isUnassignedMainSurgeon(slots: AnySurgeryResourceSlots) {
    return getMainSurgeon(slots) === undefined
}

const practitionerTypePrefixes = {
    [practitionerResourceNames.Surgeon]: 'H',
    [practitionerResourceNames.Assistant]: 'A',
    [practitionerResourceNames.FirstAssistant]: 'A',
} satisfies Record<PractitionerResourceTypeId, string>

export function getPractitionerTypePrefix(practitionerType: PractitionerResourceTypeId) {
    return practitionerTypePrefixes[practitionerType]
}

export function sortByMainSurgeonFirst(a: PractitionerResourceSlot, b: PractitionerResourceSlot) {
    if (a.assignedResourceTypeName === 'Hovedoperatør') return -1
    if (b.assignedResourceTypeName === 'Hovedoperatør') return 1
    if (a.shortName && b.shortName) return a.shortName.localeCompare(b.shortName)
    return 0
}

function isMatchingMainSurgeon(scheduledPractitioner: AnyPractitioner | undefined, mainSurgeon: PractitionerResourceSlot | undefined) {
    if (!scheduledPractitioner || !mainSurgeon) return false

    return scheduledPractitioner.short_name.toLocaleLowerCase() === mainSurgeon.shortName?.toLocaleLowerCase()
}

function hasMatchingPractitioners(scheduledPractitioners: readonly AnyPractitioner[], slots: AnySurgeryResourceSlots) {
    const mainSurgeon = getMainSurgeon(slots)
    const assistants = getAssistants(slots)

    // If only one scheduled practitioner, we don't want surgeries with assistants
    if (scheduledPractitioners.length === 1) {
        return isMatchingMainSurgeon(scheduledPractitioners.at(0), mainSurgeon) && assistants.length === 0
    }

    // If multiple scheduled practitioners, check if one matches the main surgeon and either the rest match assistants or if assistants are empty
    return scheduledPractitioners.some((scheduledPractitioner, index) => {
        if (!isMatchingMainSurgeon(scheduledPractitioner, mainSurgeon)) return false

        // If the main surgeon matches, check the assistants
        const otherScheduledPractitioners = [...scheduledPractitioners.slice(0, index), ...scheduledPractitioners.slice(index + 1)]
        const isMatchingAssistants = otherScheduledPractitioners.every(p =>
            assistants.map(assitant => assitant.shortName?.toLocaleLowerCase()).includes(p.short_name.toLocaleLowerCase())
        )

        return isMatchingAssistants || assistants.length === 0
    })
}

export function isPractitionerMatchOrUnassigned(scheduledPractitioners: readonly AnyPractitioner[], slots: AnySurgeryResourceSlots) {
    return isUnassignedMainSurgeon(slots) || hasMatchingPractitioners(scheduledPractitioners, slots)
}

export function getPractitionerMismatchReason(
    scheduledPractitioners: readonly AnyPractitioner[],
    slots: AnySurgeryResourceSlots
): 'mainSurgeon' | 'assistants' {
    const mainSurgeon = getMainSurgeon(slots)

    return scheduledPractitioners.some(practitioner => isMatchingMainSurgeon(practitioner, mainSurgeon)) ? 'assistants' : 'mainSurgeon'
}
